<template>
  <a
    class="ns-expressive-link"
    :class="[animationState, { invert }]"
    :style="dynamicStyle"
    :href="href"
    :target="target"
    @mouseover="addAnimateIn"
    @mouseout="addAnimateOut"
    @click="handleClick"
  >
    <span class="slot">
      <!-- @slot The text for the link -->
      <slot />
    </span>
    <span class="before" />
    <span class="after" />
  </a>
</template>

<script>
/**
 * Original Author: Dallin Anderson
 * Original Team: BFFs
 */
export default {
  name: 'NsExpressiveLink',
  props: {
    /**
     * The highlight color after the link is hovered. Can be a named color, a
     * hex value, a RGB value or even a css variable
     */
    afterColor: {
      type: String,
      default: '',
    },
    /**
     * The highlight color before the link is hovered Can be a named color, a
     * hex value, a RGB value or even a css variable
     */
    beforeColor: {
      type: String,
      default: '',
    },
    /**
     * Where the link takes the user when clicked
     */
    href: {
      type: String,
      default: '',
    },
    /**
     * Alters the colors for a dark background
     */
    invert: Boolean,
    /**
     * The target of the <a> tag
     */
    target: {
      type: String,
      default: '_self',
    },
  },
  data() {
    return {
      animationState: '',
    };
  },
  computed: {
    dynamicStyle() {
      return {
        '--expressive-link-bg-1-transparent': this.beforeColor,
        '--expressive-link-bg-2-transparent': this.afterColor,
      };
    },
  },
  methods: {
    /**
     * Animates the highlight in
     */
    addAnimateIn() {
      this.animationState = 'animate-in';
    },
    /**
     * Animates the highlight out
     */
    addAnimateOut() {
      this.animationState = 'animate-out';
    },
    /**
     * If no href, emits a click event
     */
    handleClick(event) {
      if (!this.href) {
        event.preventDefault();
        this.$emit('click', event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ns-expressive-link {
  --expressive-link-bg-1-transparent: rgba(113, 35, 217, 0.2);
  --expressive-link-bg-2-transparent: rgba(187, 41, 187, 0.2);

  &.invert {
    --expressive-link-bg-1-transparent: rgba(255, 255, 255, 0.1);
    --expressive-link-bg-2-transparent: rgba(255, 255, 255, 0.2);
  }
}

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.ns-expressive-link {
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0 5px;
  color: inherit;
  font-size: 21px;
  line-height: 25px;
  letter-spacing: 0.81px;
  text-decoration: none !important;
  overflow: hidden;
  cursor: pointer;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family-primary);

  &.invert {
    color: white;
  }
}

.slot {
  z-index: 1;
  position: relative;
}

.ns-expressive-link .before {
  position: absolute;
  left: -5px;
  bottom: 0;
  height: 50%;
  width: calc(100% + 10px);
  background-color: var(--expressive-link-bg-1-transparent);
}

.ns-expressive-link .after {
  display: inline-block;
  position: absolute;
  will-change: left;
  bottom: 0;
  left: 0;
  right: auto;
  height: 50%;
  width: 0;
  background-color: var(--expressive-link-bg-2-transparent);
}

.ns-expressive-link.animate-in .after {
  animation: animateIn 0.2s ease-in-out forwards;
}

.ns-expressive-link.animate-out .after {
  animation: animateOut 0.2s ease-in-out forwards;
}

@keyframes animateIn {
  0% {
    left: 0;
    right: auto;
    width: 0;
  }
  100% {
    left: 0;
    right: auto;
    width: 100%;
  }
}

@keyframes animateOut {
  0% {
    left: auto;
    right: 0;
    width: 100%;
  }
  100% {
    left: auto;
    right: 0;
    width: 0;
  }
}

@media (min-width: 1024px) {
  .ns-expressive-link {
    font-size: 15px;
    line-height: 18px;
  }
}
</style>
